import dayjs from 'dayjs';
import { Spinner } from 'flowbite-react';
import React, { useState } from 'react';
import { HiChevronRight } from 'react-icons/hi';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Container from 'src/components/shared/Container';
import PositionDetailModal from 'src/components/placementDrives/PositionDetailModal';
import { PublicDriveHeader, MobileHeader, CompanyLogo, formatPositionTitle, MobileActionBar, PositionChips, PositionSubtitle, PrimaryButton, StatusPill, DRIVE_FONT_STYLE, useDriveFont, BackLink } from 'src/components/placementDrives/shared';
import { useUserContext } from 'src/context/UserProvider';
import { useGetDriveForJobseeker, useGetPublicDrive } from 'src/hooks/drives.hooks';
import { JobseekerDrivePosition } from 'types';

const DetailRow = ({ label, value }: { label: string; value: string }) => (
	<div className="flex justify-between gap-4 py-2">
		<span className="text-[#555770] dark:text-[#B0B8C4]">{label}</span>
		<span className="font-[600] text-[#111E2C] dark:text-white text-right">{value}</span>
	</div>
);

const PlacementDriveDetailPage = () => {
	const { driveId } = useParams();
	useDriveFont();
	const navigate = useNavigate();
	const { user } = useUserContext();
	const isLoggedIn = !!user;
	const mine = useGetDriveForJobseeker(driveId, isLoggedIn);
	const pub = useGetPublicDrive(driveId, !isLoggedIn);
	const { data, isLoading, refetch, isFetching } = isLoggedIn ? mine : pub;
	const backPath = isLoggedIn ? '/my-applications' : '/placement-drives';
	const [selected, setSelected] = useState<JobseekerDrivePosition | null>(null);

	if (isLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spinner size="xl" />
			</div>
		);
	}
	if (!data?.drive) return <div className="py-24 text-center text-[#555770]">Drive not found.</div>;

	const { drive, positions, isApplicationOpen } = data;
	// Once the candidate has applied, the status page is the place to be (under review, shortlisted, ...).
	if (isLoggedIn && positions.some(position => position.applicationStatus)) return <Navigate to={`/placement-drives/${drive._id}/status`} replace />;
	const goToApply = (positionId?: string) =>
		isLoggedIn
			? navigate(`/placement-drives/${drive._id}/apply${positionId ? `?position=${positionId}` : ''}`)
			: navigate(`/placement-drives/${drive._id}/signin`);
	const noPositions = isLoggedIn && positions.length === 0;

	return (
		<>
		{isLoggedIn ? null : <PublicDriveHeader />}
		<Container className="dp-fade w-[92%] max-w-[1280px] pt-0 lg:pt-6 pb-32 lg:pb-24" style={DRIVE_FONT_STYLE}>
			<BackLink className="hidden lg:inline-flex" onClick={() => navigate(backPath)}>All drives</BackLink>
			<MobileHeader title={drive.name} onBack={() => navigate(backPath)} />

			<div className="mt-5 lg:mt-6 grid gap-8 lg:grid-cols-[1fr_380px]">
				<div>
					<span className="inline-flex items-center gap-2 px-3 py-1.5 lg:py-1 rounded-full bg-[#EEEDFD] text-[#3730A3] text-[13px] font-[600]">
						<span className="h-2 w-2 rounded-full bg-[#4F46E5]" />
						{isApplicationOpen ? `Application closes ${dayjs(drive.applicationCloseDate).format('D MMM')}` : 'Applications closed'}
					</span>
					<h1 className="mt-3 text-[34px] leading-[38px] lg:text-[40px] lg:leading-[44px] tracking-[-0.03em] font-[800] text-[#111E2C] dark:text-white">{drive.name}</h1>
					<p className="mt-2 text-[15px] lg:text-[16px] text-[#555770] dark:text-[#B0B8C4]">
						{dayjs(drive.driveDate).format('D MMMM YYYY, h:mm A')} · {drive.location}
					</p>

					<div className="mt-7 lg:mt-8 flex items-baseline justify-between gap-3">
						<h2 className="text-[17px] lg:text-[22px] whitespace-nowrap font-[700] text-[#111E2C] dark:text-white">{drive.companiesCount} companies attending</h2>
						<span className="text-[12px] lg:text-[13px] text-[#8A8BA3] whitespace-nowrap"><span className="lg:hidden">Tap</span><span className="hidden lg:inline">Click a position</span> for full details</span>
					</div>
					{noPositions ? (
							<div className="mt-3 rounded-[20px] border border-[#EEEEF5] bg-[#F6F6FA] p-6 text-center">
								<p className="text-[17px] font-[700] text-[#0F1626]">Please contact the Masai staff at the drive</p>
								<p className="mt-2 text-[15px] text-[#555770]">Refresh once they have helped you.</p>
								<PrimaryButton className="mt-5" onClick={() => refetch()} disabled={isFetching}>
									{isFetching ? 'Checking...' : 'Refresh'}
								</PrimaryButton>
							</div>
						) : null}
						<div className="mt-3 rounded-[20px] border border-[#EEEEF5] bg-white dark:bg-[#1F2937] divide-y divide-[#EEEEF5] overflow-hidden">
						{positions.map(position => (
							<React.Fragment key={position._id}>
							<button
								onClick={() => setSelected(position)}
								className="hidden lg:flex w-full items-center gap-4 p-5 text-left hover:bg-[#FAFAFC] dark:hover:bg-[#111827]"
							>
								<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={52} />
								<div className="flex-1 min-w-0">
									<p className="text-[17px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</p>
									<p className="text-[14px] text-[#555770] dark:text-[#B0B8C4]">
										<PositionSubtitle position={position} />
									</p>
								</div>
								{position.applicationStatus ? <StatusPill status={position.applicationStatus} /> : null}
								<HiChevronRight className="text-[#8A8BA3]" />
							</button>
							<button
								onClick={() => setSelected(position)}
								className="lg:hidden w-full flex items-start gap-3 p-4 text-left"
							>
								<CompanyLogo name={position.company.name} logo={position.company.logo} url={position.company.url} size={44} />
								<div className="flex-1 min-w-0">
									<p className="text-[16px] leading-[22px] font-[700] text-[#111E2C] dark:text-white">{position.company.name}</p>
									<p className="text-[14px] text-[#555770] dark:text-[#B0B8C4]">{formatPositionTitle(position.title)}</p>
									<PositionChips position={position} />
									{position.applicationStatus ? (
										<div className="mt-2.5">
											<StatusPill status={position.applicationStatus} />
										</div>
									) : null}
								</div>
								<HiChevronRight className="mt-1 text-[#8A8BA3]" />
							</button>
							</React.Fragment>
						))}
					</div>
				</div>

				<aside className="hidden lg:block h-fit rounded-[20px] border border-[#EEEEF5] bg-white dark:bg-[#1F2937] p-6 shadow-[0_2px_12px_rgba(20,20,60,0.06)]">
					<h3 className="text-[18px] font-[700] text-[#111E2C] dark:text-white pb-3 border-b border-[#EEEEF5]">Drive details</h3>
					<div className="mt-2 text-[15px]">
						<DetailRow label="Date" value={dayjs(drive.driveDate).format('D MMM YYYY')} />
						<DetailRow label="Reporting" value={dayjs(drive.driveDate).format('h:mm A')} />
						<DetailRow label="Venue" value={drive.location} />
						<DetailRow label="Companies" value={String(drive.companiesCount)} />
						<DetailRow label="Application closes" value={dayjs(drive.applicationCloseDate).format('D MMM')} />
						{drive.registrationPayment.isEnabled ? (
							<DetailRow label="Seat deposit" value={`₹${drive.registrationPayment.amount}`} />
						) : null}
					</div>
					<div className="hidden lg:block mt-4 pt-4 border-t border-[#EEEEF5]">
						<PrimaryButton className="w-full" disabled={!isApplicationOpen || noPositions} onClick={() => goToApply()}>
							Apply now
						</PrimaryButton>
					</div>
				</aside>
			</div>

			<MobileActionBar>
				<PrimaryButton className="w-full !py-4 !text-[17px]" disabled={!isApplicationOpen || noPositions} onClick={() => goToApply()}>
					Apply now
				</PrimaryButton>
			</MobileActionBar>

			<PositionDetailModal
				position={selected}
				onClose={() => setSelected(null)}
				canApply={isApplicationOpen}
				onApply={position => goToApply(position._id)}
			/>
		</Container>
		</>
	);
};

export default PlacementDriveDetailPage;
